import {
  Checkbox,
  MonthSelect,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  UserCombobox,
} from '@/components'
import { cn } from '@/lib/utils'
import type { PreviewAction } from './preview-rows'
import { codeTotals, differenceCount, differs, generated } from './preview-rows'
import type { GenerationPreview, GenerationPreviewRow } from './queries'
import {
  SALARY_GENERATION_RULES_LIVE_QUERY,
  SALARY_ITEM_GENERATION_PREVIEW_QUERY,
} from './queries'
import { BetaChip } from './beta-chip'
import { PreviewExport } from './preview-export'
import type {
  GenerationModifierSubject,
  GenerationRuleKind,
} from '../generation-rules/generation-rule-labels'
import { useGenerationRuleLabels } from '../generation-rules/generation-rule-labels'
import { GenerationTraceTooltip } from '@/modules/salary/components/generation-trace-tooltip'
import { formatPartOfDay } from '@/modules/salary/components/part-of-day'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, parseISO, startOfMonth } from 'date-fns'
import { useState } from 'react'

export const GENERATION_PREVIEW_ANCHOR = 'generation-preview'

const ACTION_TONES: Record<PreviewAction, string> = {
  create: 'border-emerald-300 bg-emerald-100 text-emerald-900',
  update: 'border-amber-300 bg-amber-100 text-amber-900',
  delete: 'border-rose-300 bg-rose-100 text-rose-900',
  unchanged: 'border-stone-300 bg-stone-100 text-stone-700',
  keep_manual: 'border-sky-300 bg-sky-100 text-sky-900',
  keep_approved: 'border-violet-300 bg-violet-100 text-violet-900',
  keep_other: 'border-stone-300 bg-stone-100 text-stone-700',
}

function useTexts() {
  return useTranslate({
    title: 'settings.generation-preview.title',
    description: 'settings.generation-preview.description',
    driver: 'settings.generation-preview.driver',
    month: 'settings.generation-preview.salary-month',
    pickDriver: 'settings.generation-preview.pick-driver',
    off: 'settings.generation-preview.off',
    notHandled: 'settings.generation-preview.not-handled',
    handled: [
      'settings.generation-preview.handled',
      { setting: '', outputs: '' },
    ],
    changes: ['settings.generation-preview.changes', { count: '' }],
    onlyChanges: 'settings.generation-preview.only-changes',
    empty: 'settings.generation-preview.empty',
    totals: 'settings.generation-preview.totals',
    noCode: 'settings.generation-preview.no-code',
    when: 'settings.generation-preview.when',
    subject: 'settings.generation-preview.subject',
    output: 'settings.generation-preview.output',
    now: 'settings.generation-preview.now',
    generated: 'settings.generation-preview.generated',
    change: 'settings.generation-preview.change',
    loading: 'common.loading',
    previewOnly: 'settings.generation-preview.preview-only',
    period: ['settings.generation-preview.period', { start: '', end: '' }],
    action: {
      create: 'settings.generation-preview.action-create',
      update: 'settings.generation-preview.action-update',
      delete: 'settings.generation-preview.action-delete',
      unchanged: 'settings.generation-preview.action-unchanged',
      keep_manual: 'settings.generation-preview.action-keep-manual',
      keep_approved: 'settings.generation-preview.action-keep-approved',
      keep_other: 'settings.generation-preview.action-keep-other',
    },
  })
}

/** Beta: a dry run of the diet and salary day rules for one driver and month. */
export const GenerationPreviewTool: React.FC = () => {
  const texts = useTexts()
  const [userId, setUserId] = useState('')
  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const [onlyChanges, setOnlyChanges] = useState(false)

  const year = month.getFullYear()
  const monthNumber = month.getMonth() + 1
  const { data, loading } = useQuery(SALARY_ITEM_GENERATION_PREVIEW_QUERY, {
    skip: !userId,
    fetchPolicy: 'network-only',
    variables: { user: userId, year, month: monthNumber },
  })
  const { data: liveData } = useQuery(SALARY_GENERATION_RULES_LIVE_QUERY)
  const preview = userId ? data?.salaryItemGenerationPreview : undefined

  return (
    <div id={GENERATION_PREVIEW_ANCHOR} className="flex flex-col gap-4">
      <section className="overflow-hidden rounded-2xl bg-white shadow-card">
        <div className="space-y-2 px-5 py-4">
          <BetaChip />
          <h2 className="text-lg font-semibold text-stone-900">
            {texts.title}
          </h2>
          <p className="text-sm text-stone-600">{texts.description}</p>
          {liveData?.salaryGenerationRulesLive === false && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {texts.previewOnly}
            </p>
          )}
        </div>
        <div className="grid gap-4 border-t border-stone-200 px-5 py-4 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase text-stone-500">
              {texts.driver}
            </span>
            <UserCombobox mode="single" value={userId} onChange={setUserId} />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase text-stone-500">
              {texts.month}
            </span>
            <MonthSelect value={month} onChange={setMonth} />
          </label>
        </div>
        {!userId && (
          <p className="border-t border-stone-200 px-5 py-4 text-sm text-stone-600">
            {texts.pickDriver}
          </p>
        )}
        {userId && loading && !preview && (
          <p className="border-t border-stone-200 px-5 py-4 text-sm text-stone-600">
            {texts.loading}
          </p>
        )}
      </section>

      {preview && (
        <PreviewResult
          preview={preview}
          onlyChanges={onlyChanges}
          onOnlyChangesChange={setOnlyChanges}
        />
      )}

      <PreviewExport year={year} month={monthNumber} />
    </div>
  )
}

const PreviewResult: React.FC<{
  preview: GenerationPreview
  onlyChanges: boolean
  onOnlyChangesChange(value: boolean): void
}> = ({ preview, onlyChanges, onOnlyChangesChange }) => {
  const texts = useTexts()
  const labels = useGenerationRuleLabels()
  const outputLabel = (output: string) =>
    labels.kind[output as GenerationRuleKind] ?? output
  const rows = onlyChanges ? preview.rows.filter(differs) : preview.rows
  const totals = codeTotals(preview.rows)

  const status = !preview.enabled
    ? texts.off
    : preview.outputs.length === 0
    ? texts.notHandled
    : texts.handled({
        setting: preview.salaryWorkTimeName ?? '',
        outputs: preview.outputs.map(outputLabel).join(', ').toLowerCase(),
      })

  return (
    <>
      <section className="overflow-hidden rounded-2xl bg-white shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-stone-900">{status}</p>
            {preview.periodStart && preview.periodEnd && (
              <p className="text-sm text-stone-600">
                {texts.period({
                  start: format(parseISO(preview.periodStart), 'dd.MM.yyyy'),
                  end: format(parseISO(preview.periodEnd), 'dd.MM.yyyy'),
                })}
              </p>
            )}
          </div>
          <span className="text-sm text-stone-600">
            {texts.changes({ count: String(differenceCount(preview.rows)) })}
          </span>
        </div>
        {totals.length > 0 && (
          <div className="border-t border-stone-200 px-5 py-4">
            <p className="mb-2 text-xs font-semibold uppercase text-stone-500">
              {texts.totals}
            </p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {totals.map(total => (
                <li
                  key={`${total.output}:${total.code?.id ?? ''}`}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="text-stone-600">
                    {outputLabel(total.output)} ·{' '}
                    {total.code
                      ? `${total.code.externalId ?? ''} ${
                          total.code.name
                        }`.trim()
                      : texts.noCode}
                  </span>
                  <span
                    className={cn(
                      'font-medium text-stone-900',
                      total.now !== total.generated && 'text-amber-900'
                    )}
                  >
                    {formatTotal(total.now)} → {formatTotal(total.generated)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-2xl bg-white shadow-card">
        <div className="flex items-center justify-end gap-2 px-5 py-3">
          <Checkbox
            id="generation-preview-only-changes"
            size="md"
            value={onlyChanges}
            onChange={checked => onOnlyChangesChange(checked === true)}
          />
          <label htmlFor="generation-preview-only-changes" className="text-sm">
            {texts.onlyChanges}
          </label>
        </div>
        {rows.length === 0 ? (
          <p className="border-t border-stone-200 px-5 py-4 text-sm text-stone-600">
            {texts.empty}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{texts.when}</TableHead>
                <TableHead>{texts.subject}</TableHead>
                <TableHead>{texts.output}</TableHead>
                <TableHead>{texts.now}</TableHead>
                <TableHead>{texts.generated}</TableHead>
                <TableHead>{texts.change}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => (
                <PreviewTableRow
                  key={`${row.subject}:${
                    row.workPeriodId ?? row.restPeriodId
                  }:${row.output}`}
                  row={row}
                  subjectLabel={
                    labels.subject[row.subject as GenerationModifierSubject]
                  }
                  outputLabel={outputLabel(row.output)}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </section>
    </>
  )
}

const PreviewTableRow: React.FC<{
  row: GenerationPreviewRow
  subjectLabel: string
  outputLabel: string
}> = ({ row, subjectLabel, outputLabel }) => {
  const texts = useTexts()
  const rules = generated(row)
  const action = row.action as PreviewAction
  const start = parseISO(row.datetimeStart)
  const end = parseISO(row.datetimeEnd)
  const sameDay = format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">
        {format(start, 'EEE dd.MM HH:mm')} –{' '}
        {format(end, sameDay ? 'HH:mm' : 'EEE dd.MM HH:mm')}
      </TableCell>
      <TableCell>{subjectLabel}</TableCell>
      <TableCell>{outputLabel}</TableCell>
      <TableCell>
        <ValueCell value={row.currentValue} code={row.currentSalaryCode} />
      </TableCell>
      <TableCell>
        <ValueCell value={rules.value} code={rules.code} />
      </TableCell>
      <TableCell>
        <span
          className={cn(
            'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold whitespace-nowrap',
            ACTION_TONES[action]
          )}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {texts.action[action]}
        </span>
      </TableCell>
      <TableCell>
        <GenerationTraceTooltip
          trace={row.generatedTrace ?? row.currentTrace}
        />
      </TableCell>
    </TableRow>
  )
}

const ValueCell: React.FC<{
  value: number | null
  code: { name: string } | null
}> = ({ value, code }) =>
  value === null ? (
    <span className="text-stone-400">–</span>
  ) : (
    <span className="flex flex-col">
      <span className="font-medium text-stone-900">
        {formatPartOfDay(value)}
      </span>
      {code && <span className="text-xs text-stone-500">{code.name}</span>}
    </span>
  )

function formatTotal(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 3 })
}
